<template>
    <div class="flex-1">
        <skeleton v-if="planBookingPage === undefined" />
        <not-found v-else-if="planBookingPage === null" />
        <not-found
            v-else-if="planBookingPage.__typename === 'InvalidBookingPage'"
            :title="planBookingPage.title"
            :reason="planBookingPage.reason"
        />
        <not-found
            v-else-if="!planBookingPage.active"
            :reason="t('pages.inactivePlan')"
        />
        <purchase-gift-card
            v-else
            :plan="planBookingPage"
        />
    </div>
</template>

<script setup lang="ts">
import { definePageMeta } from '#imports'
import { useRoute } from '#app'
import { computed, onServerPrefetch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { useRpQuery } from '~/composables/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import Skeleton from 'primevue/skeleton'
import NotFound from '~/components/NotFound/NotFound.vue'
import PurchaseGiftCard from '~/components/PurchaseContract/GiftCard/PurchaseGiftCard.vue'

import { useSelectedLanguage } from '~/composables/use-selected-language'
import { useI18n } from 'vue-i18n'
import { useCanonicalPurchaseLink } from '~/composables/use-canonical-link'

// Offering pages are public; customer-specific gift-card URLs remain excluded by default.
definePageMeta({ searchIndexing: 'public' })

const route = useRoute()
const slug = computed(() => route.params.slug)
const activeFacility = useActiveFacility()
const { t } = useI18n({ useScope: 'global' })

// Direct purchase routes are canonical over duplicate storefront node URLs.
useCanonicalPurchaseLink('GIFTCARD', slug)

const query = useRpQuery()
const selectedLanguage = useSelectedLanguage()
const { data, suspense } = useQuery({
    queryKey: ['GiftCardsPageQuery', slug, activeFacility, selectedLanguage],
    queryFn: () =>
        query(
            graphql(/* GraphQL */ `
                query GiftCardsPageQuery(
                    $bookingInput: PlanBookingPageInput!
                    $facilityId: ID!
                    $input: SimpleSpokeInput
                    $language: Language!
                ) {
                    planBookingPage(input: $bookingInput) {
                        __typename
                        ... on InvalidBookingPage {
                            title
                            reason
                        }
                        ... on Plan {
                            ...PurchaseGiftCardPlanFragment
                            active
                        }
                    }
                }
            `),
            {
                facilityId: activeFacility.value.id,
                input: {
                    facilityId: activeFacility.value.id
                },
                language: selectedLanguage.value,
                bookingInput: {
                    slug: slug.value,
                    facilityId: activeFacility.value.id,
                    planType: 'GIFTCARD'
                }
            }
        )
})

const planBookingPage = computed(() => {
    if (data.value === undefined) {
        return undefined
    }

    if (data.value.planBookingPage === null) {
        return null
    }

    return data.value.planBookingPage
})

onServerPrefetch(async () => {
    // SSR needs the booking-page query resolved before rendering the purchasable plan.
    await suspense()
})
</script>
